import React, { useCallback, useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const IMAGES = [{
  src: 'https://cdn.magicpatterns.com/uploads/4gjyZwv8jwkwM2bejM3iim/1.png',
  alt: 'Página del Kit 1'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/96vTB1FZWPrF1QAtJ8hJRG/2.png',
  alt: 'Página del Kit 2'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/712NgzEFT1Ntea69eeS8S4/3.png',
  alt: 'Página del Kit 3'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/81bx1VoGX8YAzMPw4gqR4A/4.png',
  alt: 'Página del Kit 4'
}];
export function KitGallery() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [direction, setDirection] = useState(0);
  const openLightbox = (index: number) => {
    setSelectedIndex(index);
    setDirection(0);
  };
  const closeLightbox = () => {
    setSelectedIndex(null);
  };
  const paginate = useCallback((newDirection: number) => {
    if (selectedIndex === null) return;
    setDirection(newDirection);
    let newIndex = selectedIndex + newDirection;
    if (newIndex < 0) newIndex = IMAGES.length - 1;
    if (newIndex >= IMAGES.length) newIndex = 0;
    setSelectedIndex(newIndex);
  }, [selectedIndex]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      if (e.key === 'ArrowLeft') paginate(-1);
      if (e.key === 'ArrowRight') paginate(1);
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, paginate]);
  // Prevent scrolling when lightbox is open
  useEffect(() => {
    if (selectedIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedIndex]);
  const variants = {
    enter: (direction: number) => {
      return {
        x: direction > 0 ? 1000 : -1000,
        opacity: 0
      };
    },
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => {
      return {
        zIndex: 0,
        x: direction < 0 ? 1000 : -1000,
        opacity: 0
      };
    }
  };
  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };
  return <>
      {/* Thumbnail Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-21">
        {IMAGES.map((img, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.1,
        duration: 0.4
      }} className="cursor-pointer group" onClick={() => openLightbox(i)} data-id="element-22">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white transition-transform duration-300 md:group-hover:scale-105 md:group-hover:shadow-md" data-id="element-23">
              <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-auto block object-cover aspect-[3/4]" data-id="element-24" />
            </div>
          </motion.div>)}
      </div>

      {/* Lightbox Overlay */}
      <AnimatePresence initial={false} custom={direction} data-id="element-25">
        {selectedIndex !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 md:p-8" onClick={closeLightbox} data-id="element-26">
            {/* Close Button */}
            <button className="absolute top-4 right-4 md:top-6 md:right-6 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 transition-colors z-50" onClick={e => {
          e.stopPropagation();
          closeLightbox();
        }} data-id="element-27">
              <XIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-28" />
            </button>

            {/* Navigation Arrows */}
            <button className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 md:p-3 transition-colors z-50" onClick={e => {
          e.stopPropagation();
          paginate(-1);
        }} data-id="element-29">
              <ChevronLeftIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-30" />
            </button>

            <button className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 md:p-3 transition-colors z-50" onClick={e => {
          e.stopPropagation();
          paginate(1);
        }} data-id="element-31">
              <ChevronRightIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-32" />
            </button>

            {/* Image Container */}
            <div className="relative w-full h-full flex items-center justify-center overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-33">
              <AnimatePresence initial={false} custom={direction} data-id="element-34">
                <motion.img key={selectedIndex} src={IMAGES[selectedIndex].src} alt={IMAGES[selectedIndex].alt} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={1} onDragEnd={(e, {
              offset,
              velocity
            }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }} className="absolute max-w-full max-h-full object-contain rounded-lg shadow-2xl" data-id="element-35" />
              </AnimatePresence>
            </div>

            {/* Counter */}
            <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 text-white/80 bg-black/50 px-4 py-1.5 rounded-full text-sm font-medium tracking-wide" data-id="element-36">
              {selectedIndex + 1} / {IMAGES.length}
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}